<template>
  <ListItem
    :to="{ name: 'bookingEdit', params: { id: booking.id } }"
    :drop-down-items="showMenu ? dropdownItems : undefined"
  >
    <div class="flex w-full items-start gap-2 text-sm max-sm:flex-col md:items-center md:gap-4">
      <div
        class="flex basis-[12%] items-center gap-2 self-stretch rounded-sm text-center text-sm font-semibold text-obsidian-700 sm:flex-col sm:gap-1"
      >
        <div><span class="text-obsidian-400"># </span>{{ booking.bookingNumber }}</div>
        <BookingStatusBadge :status="booking.status" />
      </div>
      <div class="basis-[50%]">
        <p class="font-semibold">
          {{ booking.customerData?.firstName ?? 'n/a' }}
          {{ booking.customerData?.lastName ?? 'n/a' }}
        </p>
        <p>
          <LicencePlate>{{ allocation.car?.licencePlate }}</LicencePlate>
          {{ allocation.car?.vehicleType?.make.name }}
          {{ allocation.car?.vehicleType?.model }}
        </p>
      </div>

      <div class="grid w-full basis-[30%] max-sm:grid-cols-2">
        <div class="mb-1">
          <span><ArrowRightFromLineIcon class="inline-icon text-hazel-500" />{{ startDate }}</span>
        </div>
        <div>
          <span><ArrowLeftToLineIcon class="inline-icon text-hazel-500" />{{ endDate }}</span>
        </div>
      </div>
    </div>
  </ListItem>
</template>

<script setup lang="ts">
import type { DropdownItem } from '@/components/DropdownDotted.vue';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import type { ManyBookingsBooking } from '@/entities/bookings/many-bookings-booking.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useResendBookingMails } from '@/hooks/use-resend-booking-mails';
import { useI18n } from 'vue-i18n';
import { useRefreshBookingConfirmation } from '@/queries/use-bookings';
import { computed } from 'vue';
import LicencePlate from '@/components/LicencePlate.vue';
import { ArrowLeftToLineIcon, ArrowRightFromLineIcon } from 'lucide-vue-next';
import BookingStatusBadge from '@/components/badges/BookingStatusBadge.vue';
import ListItem from '@/components/ListItem.vue';
import { useAllocationOfBookingByDate } from '@/hooks/use-allocation-of-booking-by-date.ts';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { downloadBlob, fetchBlobFromUrl, fetchPdfBlobFromUrl } from '@/utils/download-file';

const props = withDefaults(
  defineProps<{
    booking: ManyBookingsBooking;
    showMenu?: boolean;
  }>(),
  { showMenu: true },
);
const { t } = useI18n();
const dateFormat = 'dd.MM.yyyy - HH:mm';

const startDate = useFormattedDate(() => props.booking.startDate, dateFormat);
const endDate = useFormattedDate(() => props.booking.endDate, dateFormat);

const {
  resendConfirmationEmail,
  resendContractEmail,
  resendUpdatedContractImmediately,
  sendRentalKeyLink,
} = useResendBookingMails(props.booking.id);
const { mutateAsync: refreshBookingConfirmation, isPending: isRefreshingBookingConfirmation } =
  useRefreshBookingConfirmation();
const pdfPreview = usePdfPreview();

const allocation = useAllocationOfBookingByDate(
  computed(() => props.booking),
  new Date(),
);

const downloadContract = () => {
  if (!props.booking.contractFileUrl) return;
  void pdfPreview.openPdfPreview({
    title: t('contractPdfTitle', { bookingNumber: props.booking.bookingNumber }),
    fileName: `booking-${props.booking.bookingNumber}-contract.pdf`,
    load: () => fetchPdfBlobFromUrl(props.booking.contractFileUrl!),
    errorMessage: t('couldNotLoadPdf'),
  });
};

const downloadContractDirect = async () => {
  if (!props.booking.contractFileUrl) return;
  const blob = await fetchBlobFromUrl(props.booking.contractFileUrl);
  await downloadBlob(blob, `booking-${props.booking.bookingNumber}-contract.pdf`);
};

const downloadBookingConfirmation = async () => {
  if (!props.booking.confirmationFileUrl) return;
  await pdfPreview.openPdfPreview({
    title: t('bookingConfirmationPdfTitle', { bookingNumber: props.booking.bookingNumber }),
    fileName: `booking-${props.booking.bookingNumber}-confirmation.pdf`,
    load: async () => {
      const { fileUrl } = await refreshBookingConfirmation(props.booking.id);
      return fetchPdfBlobFromUrl(fileUrl);
    },
    errorMessage: t('couldNotLoadPdf'),
  });
};

const downloadBookingConfirmationDirect = async () => {
  if (!props.booking.confirmationFileUrl) return;
  const { fileUrl } = await refreshBookingConfirmation(props.booking.id);
  const blob = await fetchBlobFromUrl(fileUrl);
  await downloadBlob(blob, `booking-${props.booking.bookingNumber}-confirmation.pdf`);
};

const dropdownItems = computed<DropdownItem[]>(() => {
  const menuItems: DropdownItem[] = [];
  if (
    props.booking.status === BookingStatus.CONFIRMED ||
    props.booking.status === BookingStatus.CAR_RETURNED ||
    props.booking.status === BookingStatus.HANDED_OVER
  ) {
    menuItems.push({
      title: t('resendConfirmation'),
      onClick: resendConfirmationEmail,
    });
  }
  if (
    (props.booking.status === BookingStatus.CONFIRMED ||
      props.booking.status === BookingStatus.HANDED_OVER ||
      props.booking.status === BookingStatus.CAR_RETURNED) &&
    props.booking.confirmationFileUrl
  ) {
    menuItems.push({
      title: t('downloadConfirmation'),
      onClick: downloadBookingConfirmation,
    });
    menuItems.push({
      title: t('downloadConfirmationDirect'),
      onClick: downloadBookingConfirmationDirect,
    });
  }
  if (
    props.booking.status === BookingStatus.CAR_RETURNED ||
    props.booking.status === BookingStatus.HANDED_OVER
  ) {
    menuItems.push({
      title: t('resendContract'),
      onClick: () => resendContractEmail(),
    });
    menuItems.push({
      title: t('resendUpdatedContractImmediately'),
      onClick: () => resendUpdatedContractImmediately(),
    });
  }
  if (props.booking.status === BookingStatus.HANDED_OVER) {
    menuItems.push({
      title: t('sendRentalKeyLink'),
      onClick: sendRentalKeyLink,
    });
  }
  if (
    (props.booking.status === BookingStatus.CAR_RETURNED ||
      props.booking.status === BookingStatus.HANDED_OVER) &&
    props.booking.contractFileUrl
  ) {
    menuItems.push({
      title: t('downloadContract'),
      onClick: downloadContract,
    });
    menuItems.push({
      title: t('downloadContractDirect'),
      onClick: downloadContractDirect,
    });
  }
  return menuItems;
});
</script>

<i18n lang="json">
{
  "en": {
    "vehicle": "Vehicle",
    "customer": "Customer",
    "start": "Start:",
    "end": "End:",
    "bookingNumber": "Bnr.",
    "licencePlate": "Lp.",
    "resendConfirmation": "Resend Confirmation",
    "resendContract": "Resend Contract",
    "resendUpdatedContractImmediately": "Regenerate + resend contract now",
    "sendRentalKeyLink": "Send rental overview link",
    "downloadContract": "Show Contract",
    "downloadContractDirect": "Download Contract",
    "downloadConfirmation": "Show Confirmation",
    "downloadConfirmationDirect": "Download Confirmation",
    "contractPdf": "Contract",
    "bookingConfirmationPdf": "Booking Confirmation",
    "contractPdfTitle": "Contract for Booking #{bookingNumber}",
    "bookingConfirmationPdfTitle": "Booking Confirmation #{bookingNumber}",
    "couldNotLoadPdf": "The PDF could not be loaded. Please try again."
  },
  "de": {
    "vehicle": "Fahrzeug",
    "customer": "Kunde",
    "start": "Start:",
    "end": "Ende:",
    "bookingNumber": "Bnr.",
    "licencePlate": "Knz.",
    "resendConfirmation": "Bestätigung erneut senden",
    "resendContract": "Vertrag erneut senden",
    "resendUpdatedContractImmediately": "Vertrag jetzt neu erstellen + senden",
    "sendRentalKeyLink": "Mietübersicht senden",
    "downloadContract": "Vertrag anzeigen",
    "downloadContractDirect": "Vertrag herunterladen",
    "downloadConfirmation": "Bestätigung anzeigen",
    "downloadConfirmationDirect": "Bestätigung herunterladen",
    "contractPdf": "Vertrag",
    "bookingConfirmationPdf": "Buchungsbestätigung",
    "contractPdfTitle": "Vertrag zur Buchung #{bookingNumber}",
    "bookingConfirmationPdfTitle": "Buchungsbestätigung #{bookingNumber}",
    "couldNotLoadPdf": "Das PDF konnte nicht geladen werden. Bitte versuche es erneut."
  },
  "es": {
    "vehicle": "Vehículo",
    "customer": "Cliente",
    "start": "Inicio:",
    "end": "Fin:",
    "bookingNumber": "N.º res.",
    "licencePlate": "Matr.",
    "resendConfirmation": "Reenviar confirmación",
    "resendContract": "Reenviar contrato",
    "resendUpdatedContractImmediately": "Regenerar y reenviar contrato ahora",
    "sendRentalKeyLink": "Enviar resumen de alquiler",
    "downloadContract": "Ver contrato",
    "downloadContractDirect": "Descargar contrato",
    "downloadConfirmation": "Ver confirmación",
    "downloadConfirmationDirect": "Descargar confirmación",
    "contractPdf": "Contrato",
    "bookingConfirmationPdf": "Confirmación de reserva",
    "contractPdfTitle": "Contrato de la reserva n.º {bookingNumber}",
    "bookingConfirmationPdfTitle": "Confirmación de reserva n.º {bookingNumber}",
    "couldNotLoadPdf": "No se pudo cargar el PDF. Inténtelo de nuevo."
  }
}
</i18n>
